import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import hosys from "@/api/api";
import { Button } from "@/components/ui/button";
import { createPageUrl } from "@/utils";
import { Link } from "react-router-dom";
import {
  X, Send, Bot, User, Loader2, Lightbulb, Clock,
  Trash2, MessageCircle, FileText, ChevronRight,
  Thermometer, SprayCan, AlertTriangle, ChefHat,
  ClipboardCheck, Droplet, BarChart3, History,
  Sparkles, BookOpen,
} from "lucide-react";
import DocumentDrafter from "./DocumentDrafter";

// ─── Context per mòdul ──────────────────────────────────────────────────────
const CONTEXT_PROMPTS = {
  Dashboard: "L'usuari es troba al tauler de control principal. Pot tenir alertes actives, temperatures incorrectes o neteges pendents.",
  Temperatures: "L'usuari es troba al mòdul de control de temperatures. Pot tenir preguntes sobre rangs correctes, accions correctives per temperatures fora de rang, o com registrar temperatures de recepció.",
  Cleaning: "L'usuari es troba al mòdul de neteja i desinfecció. Pot tenir dubtes sobre protocols, productes, freqüències o procediments de neteja.",
  Incidents: "L'usuari es troba al mòdul de gestió d'incidències. Pot necessitar ajuda per redactar accions correctives, classificar la gravetat o definir mesures preventives.",
  RecipeSheets: "L'usuari es troba al mòdul de fitxes tècniques. Pot tenir dubtes sobre com completar fitxes, al·lèrgens obligatoris, informació nutricional o rendibilitat.",
  Documents: "L'usuari es troba al mòdul de gestió documental. Pot necessitar saber quins documents cal tenir, com organitzar-los o quan renovar-los.",
  Verifications: "L'usuari es troba al mòdul de verificacions APPCC. Pot tenir dubtes sobre la freqüència, tipus de verificació o com documentar els resultats.",
  ChlorineControl: "L'usuari es troba al mòdul de control de clor de l'aigua. Valors correctes: 0.5-1.5 ppm per a ús alimentari.",
  TimeControl: "L'usuari es troba al mòdul de control horari del personal.",
  Ingredients: "L'usuari es troba al mòdul de gestió d'ingredients i costos.",
  ComplianceDashboard: "L'usuari es troba al tauler de compliment normatiu APPCC.",
  NotificationCenter: "L'usuari es troba al centre de notificacions i alertes del sistema.",
};

// ─── Preguntes ràpides per pàgina ────────────────────────────────────────────
const PAGE_QUICK_QUESTIONS = {
  Temperatures: [
    "Quina temperatura ha de tenir una nevera?",
    "Quina temperatura ha de tenir un congelador?",
    "Què fer si la temperatura és incorrecta?",
    "Com registrar temperatura de recepció de mercaderies?",
    "Quina temperatura han de tenir els menjars calents?",
  ],
  Cleaning: [
    "Diferència entre neteja i desinfecció?",
    "Cada quant s'ha de netejar la campana extractora?",
    "Quins productes usar per desinfectar superfícies?",
    "Com netejar correctament una taula de treball?",
    "Protocols de neteja per a cambres frigorífiques?",
  ],
  Incidents: [
    "Com redactar una acció correctiva?",
    "Quan classificar una incidència com a crítica?",
    "Quines causes pot tenir una contaminació creuada?",
    "Mesures preventives per a incidències de temperatura?",
    "Com documentar una incidència correctament?",
  ],
  RecipeSheets: [
    "Quins al·lèrgens he d'indicar obligatòriament?",
    "Com calcular el cost d'un plat?",
    "Quina informació ha de tenir una fitxa tècnica?",
    "Quin marge de benefici és recomanable?",
    "Com es calcula la vida útil d'un producte?",
  ],
  Documents: [
    "Quins documents necessita un restaurant obligatòriament?",
    "Amb quina freqüència renovar certificats sanitaris?",
    "Quin és el pla de neteja APPCC?",
    "Quins permisos necessita un local de restauració?",
  ],
  Verifications: [
    "Quines verificacions APPCC són obligatòries?",
    "Amb quina freqüència fer verificacions?",
    "Què és un punt de control crític (PCC)?",
    "Com documentar una verificació incorrecta?",
  ],
  ChlorineControl: [
    "Quin valor de clor és correcte per a ús alimentari?",
    "Cada quant s'ha de controlar el clor de l'aigua?",
    "Què fer si el clor és massa alt o massa baix?",
  ],
  Dashboard: [
    "Quina temperatura ha de tenir una nevera?",
    "Com actuar davant d'una inspecció sanitària?",
    "Quins controls diaris he de fer al restaurant?",
    "Què és el sistema APPCC?",
    "Com prevenir contaminació creuada?",
  ],
};

const DEFAULT_QUICK_QUESTIONS = [
  "Quina temperatura ha de tenir una nevera?",
  "Com actuar davant d'una inspecció sanitària?",
  "Quins al·lèrgens he de controlar?",
  "Cada quant s'ha de fer neteja a la cuina?",
  "Què és el sistema APPCC?",
  "Com redactar una acció correctiva?",
];

// ─── System prompt ────────────────────────────────────────────────────────────
const SYSTEM_PROMPT = `Ets l'assistent IA de compliance i operacions de Ho-Sys, una plataforma SaaS d'hostaleria per a restaurants, bars, cafeteries i negocis alimentaris a Espanya i Catalunya.

MISSIÓ: Respondre preguntes amb respostes precises, pràctiques, operacionals i jurídicament fonamentades sobre APPCC, seguretat alimentària, higiene, al·lèrgens, traçabilitat, neteja, temperatures, personal, plagues, manteniment, inspeccions i documentació.

IMPORTANT:
- NO ets advocat ni inspector sanitari.
- Mai inventis obligacions legals.
- Sempre distingeix entre: requisit legal, recomanació oficial, i bona pràctica Ho-Sys.

JERARQUIA DE FONTS:
1. Legislació vinculant: Reglaments UE (CE 178/2002, CE 852/2004, UE 1169/2011, UE 2021/382), BOE (Llei 17/2011, RD 126/2015, RD 1021/2022)
2. Autoritats oficials: AESAN, ACSA
3. Guies oficials: ACSA (APPCC, prerequisits, restaurants, al·lèrgens, neteja)
4. Dades internes Ho-Sys

TEMPERATURES APPCC:
- Nevera: 0-5°C (mai superar 8°C) | Congelador: ≤-18°C
- Menjars calents: >65°C | Freds preparats: <8°C
- Recepció carn: ≤4°C | Peix: 0-2°C | Congelats: ≤-15°C
- Oli de fregir: màx 180°C
- Refredament ràpid: >65°C a <10°C en <2h
- Regeneració: >65°C en <2h | Descongelació: sempre a nevera

FORMAT DE RESPOSTA:
A) Resposta directa B) Base legal/oficial C) Aplicació pràctica D) Acció a Ho-Sys E) Dades que falten

Etiqueta: "Obligatori" / "Recomanable" / "Acció a Ho-Sys"

Quan citis normativa:
- "Base legal: Reglament (CE) 852/2004, annex II"
- "Base oficial: ACSA, guia d'autocontrol APPCC"
- No reclamis articles no verificats

ÀREES D'ALT RISC (sigues especialment estricte):
Al·lèrgens, temperatures, intoxicació, traçabilitat/retirades, contaminació creuada, aliments crus, refredament/reescalfament, plagues, químics de neteja, exclusió personal malalt, inspeccions, etiquetatge, "sense gluten"/"vegà".

MAI facis:
- Inventar articles o sancions
- Dir que una recomanació és obligació legal
- Aprovar productes sense documentació
- Dir "tot bé" sense comprovar dades
- Dir "100% segur" en al·lèrgies

MÒDULS HO-SYS (usa format [LINK:Nom:text]):
Dashboard, Temperatures, Cleaning, Incidents, RecipeSheets, Documents, Verifications, ChlorineControl, TimeControl, Ingredients, ComplianceDashboard, PestControl, Maintenance, Traceability, RiskDashboard

Respon en l'idioma de l'usuari. Per defecte en català per a restaurants de Catalunya.`;

// ─── Renderitzat de missatges ─────────────────────────────────────────────────
function parseLinks(text) {
  const parts = [];
  const regex = /\[LINK:(\w+):([^\]]+)\]/g;
  let lastIndex = 0, match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push({ type: "text", content: text.slice(lastIndex, match.index) });
    parts.push({ type: "link", page: match[1], label: match[2] });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push({ type: "text", content: text.slice(lastIndex) });
  return parts;
}

function RichText({ text }) {
  const lines = text.split("\n");
  return (
    <span className="block space-y-1">
      {lines.map((line, li) => {
        const lineKey = `line-${li}-${line.length}`;
        if (!line.trim()) return <br key={lineKey} />;
        const parts = parseLinks(line);
        const rendered = parts.map((part, i) => {
          if (part.type === "link") {
            return (
              <Link key={`${lineKey}-link-${i}-${part.page}`} to={createPageUrl(part.page)}
                className="inline-flex items-center gap-0.5 text-[#7B2D3F] underline underline-offset-2 font-semibold hover:opacity-75 transition-opacity">
                {part.label} <ChevronRight className="w-3 h-3" />
              </Link>
            );
          }
          // Render **bold** inline
          const boldParts = part.content.split(/(\*\*[^*]+\*\*)/g);
          return boldParts.map((bp, bi) =>
            bp.startsWith("**") && bp.endsWith("**")
              ? <strong key={`${lineKey}-b-${i}-${bi}`}>{bp.slice(2, -2)}</strong>
              : <span key={`${lineKey}-t-${i}-${bi}`}>{bp}</span>
          );
        });
        // Detect list items
        if (line.trimStart().startsWith("- ") || line.trimStart().match(/^\d+\./)) {
          return <div key={lineKey} className="flex gap-1.5"><span className="text-gray-400 flex-shrink-0 mt-0.5">•</span><span>{rendered}</span></div>;
        }
        return <div key={lineKey}>{rendered}</div>;
      })}
    </span>
  );
}

// ─── Historial ───────────────────────────────────────────────────────────────
const HISTORY_KEY = "aeh_assistant_history";
function loadHistory() {
  try { return JSON.parse(sessionStorage.getItem(HISTORY_KEY) || "[]"); } catch { return []; }
}
function saveHistory(messages) {
  try { sessionStorage.setItem(HISTORY_KEY, JSON.stringify(messages.slice(-60))); } catch {}
}

const WELCOME_MSG = {
  role: "assistant",
  content: "Hola! Soc **HoSys**, l'assistent IA 👋\n\nPot ajudar-te amb:\n- **Temperatures** i controls APPCC\n- Resolució d'**incidències**\n- Protocols de **neteja**\n- Preparació d'**inspeccions**\n- **Rendibilitat** de la carta\n- I qualsevol dubte sobre el teu restaurant\n\nEn què et puc ajudar avui?",
  ts: Date.now(),
};

// ─── Component principal ──────────────────────────────────────────────────────
export default function AIAssistant({ currentPageName }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("chat");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const initialized = useRef(false);
  const sendMessageRef = useRef(null);

  useEffect(() => {
    initialized.current = true;
    const handleOpen = () => { 
      setMessages([{ ...WELCOME_MSG, ts: Date.now() }]); 
      setOpen(true); 
      setTab("chat"); 
    };
    const handleQuestion = (e) => { 
      setMessages([{ ...WELCOME_MSG, ts: Date.now() }]); 
      setOpen(true); 
      setTab("chat"); 
      sendMessageRef.current?.(e.detail); 
    };
    window.addEventListener("open-assistant", handleOpen);
    window.addEventListener("assistant-question", handleQuestion);
    return () => {
      window.removeEventListener("open-assistant", handleOpen);
      window.removeEventListener("assistant-question", handleQuestion);
    };
  }, []);

  useEffect(() => {
    if (initialized.current && messages.length > 0) saveHistory(messages);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        if (tab === "chat") inputRef.current?.focus();
      }, 120);
    }
  }, [open, tab]);

  useEffect(() => {
    if (open && messages.length > 0) {
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  const pageContext = CONTEXT_PROMPTS[currentPageName] || "";
  const quickQuestions = PAGE_QUICK_QUESTIONS[currentPageName] || DEFAULT_QUICK_QUESTIONS;

  const sendMessage = async (text) => {
    const userMsg = text.trim();
    if (!userMsg || loading) return;
    const newMessages = [...messages, { role: "user", content: userMsg, ts: Date.now() }];
    setMessages(newMessages);
    setInput("");
    setLoading(true);
    try {
      const conversationHistory = newMessages
        .slice(-12)
        .map(m => `${m.role === "user" ? "Usuari" : "Assistent"}: ${m.content}`)
        .join("\n\n");
      const prompt = `${SYSTEM_PROMPT}\n\n${pageContext ? `CONTEXT ACTUAL DE L'USUARI: ${pageContext}\n\n` : ""}CONVERSA:\n${conversationHistory}\n\nAssistent:`;
      const response = await hosys.integrations.Core.InvokeLLM({ prompt });
      setMessages(prev => [...prev, { role: "assistant", content: response, ts: Date.now() }]);
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: "Ho sento, hi ha hagut un error. Torna-ho a intentar.", ts: Date.now(), error: true }]);
    } finally {
      setLoading(false);
    }
  };

  // Keep ref updated so event listeners can call it
  useEffect(() => { sendMessageRef.current = sendMessage; });

  const clearHistory = () => {
    setMessages([{ ...WELCOME_MSG, ts: Date.now() }]);
    sessionStorage.removeItem(HISTORY_KEY);
    setShowHistory(false);
  };

  // Group messages for history view (by day)
  const historyGroups = useMemo(() => messages.reduce((acc, msg) => {
    const day = msg.ts ? new Date(msg.ts).toLocaleDateString("ca-ES") : "Avui";
    if (!acc[day]) acc[day] = [];
    acc[day].push(msg);
    return acc;
  }, {}), [messages]);

  return (
    <>
      {/* FAB */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-24 right-4 lg:bottom-6 lg:right-6 z-50 w-14 h-14 rounded-2xl bg-blue-500 hover:bg-blue-600 shadow-xl shadow-blue-500/40 flex items-center justify-center transition-all hover:scale-105 active:scale-95"
        >
          <Bot className="w-6 h-6 text-white" />
          <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-emerald-400 rounded-full border-2 border-white animate-pulse" />
        </button>
      )}

      {/* Panel */}
      {open && (
        <div className="fixed bottom-16 right-0 sm:bottom-6 sm:right-6 z-50 w-full sm:w-[420px] h-[calc(100vh-80px)] sm:h-[620px] flex flex-col bg-gradient-to-b from-white to-gray-50 sm:rounded-3xl shadow-2xl border border-gray-200 overflow-hidden">

          {/* Header */}
          <div className="bg-gradient-brand px-4 py-3 flex items-center gap-2 flex-shrink-0">
            <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center">
              <Bot className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-bold text-sm leading-tight">Assistent AEH</p>
              <p className="text-white/65 text-[11px]">Expert en hostaleria · APPCC · Seguretat alimentària</p>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setShowHistory(!showHistory)}
                className={`p-2 rounded-xl transition-colors hidden sm:flex ${showHistory ? "bg-white/20 text-white" : "hover:bg-white/10 text-white/70 hover:text-white"}`}
                title="Historial de consultes">
                <History className="w-4 h-4" />
              </button>
              <button onClick={clearHistory}
                className="p-2 rounded-xl hover:bg-white/10 transition-colors text-white/70 hover:text-white hidden sm:flex"
                title="Esborrar conversa">
                <Trash2 className="w-4 h-4" />
              </button>
              <button onClick={(e) => { e.stopPropagation(); setOpen(false); }}
                className="p-2.5 sm:p-2 rounded-xl bg-white text-[#7B2D3F] hover:bg-red-500 hover:text-white transition-colors flex-shrink-0 ml-2 font-bold shadow-lg">
                <X className="w-5 sm:w-4 h-5 sm:h-4" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-100 flex-shrink-0 bg-white">
            {[
              { id: "chat", icon: MessageCircle, label: "Xat" },
              { id: "draft", icon: Sparkles, label: "Redactar" },
              { id: "guide", icon: BookOpen, label: "Guia ràpida" },
            ].map(t => (
              <button key={t.id} onClick={() => { setTab(t.id); setShowHistory(false); }}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-[11px] font-semibold transition-all ${
                  tab === t.id ? "text-[#7B2D3F] border-b-2 border-[#7B2D3F]" : "text-gray-400 hover:text-gray-600"
                }`}>
                <t.icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            ))}
          </div>

          {/* ── CHAT TAB ── */}
          {tab === "chat" && (
            <>
              {/* Context badge */}
              {pageContext && !showHistory && (
                <div className="px-4 py-1.5 bg-[#7B2D3F]/5 border-b border-gray-50 flex-shrink-0">
                  <p className="text-[11px] text-[#7B2D3F] font-semibold">
                    📍 Context: {currentPageName || "General"}
                  </p>
                </div>
              )}

              {/* History panel */}
              {showHistory ? (
                <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Historial de consultes</p>
                    <span className="text-[11px] text-gray-400">{messages.filter(m => m.role === "user").length} preguntes</span>
                  </div>
                  {Object.entries(historyGroups).map(([day, msgs]) => (
                    <div key={day}>
                      <p className="text-[10px] font-bold text-gray-300 uppercase tracking-wider mb-2">{day}</p>
                      <div className="space-y-2">
                        {msgs.filter(m => m.role === "user").map((msg, i) => (
                          <button
                            key={`${day}-msg-${i}-${msg.content.slice(0, 15)}`}
                            onClick={() => { sendMessage(msg.content); setShowHistory(false); }}
                            className="w-full text-left text-xs bg-gray-50 hover:bg-[#f9f0f2] border border-gray-100 hover:border-[#7B2D3F]/20 rounded-xl px-3 py-2.5 text-gray-700 transition-all flex items-start gap-2 group">
                            <MessageCircle className="w-3.5 h-3.5 text-gray-300 group-hover:text-[#7B2D3F] flex-shrink-0 mt-0.5" />
                            <span className="truncate">{msg.content}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                /* Messages */
                <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
                  {messages.map((msg, i) => (
                    <div key={`member-${i}`} className={`flex gap-2.5 ${msg.role === "user" ? "flex-row-reverse" : "flex-row"}`}>
                      <div className={`w-7 h-7 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        msg.role === "user" ? "bg-[#7B2D3F]" : "bg-gray-100"
                      }`}>
                        {msg.role === "user"
                          ? <User className="w-3.5 h-3.5 text-white" />
                          : <Bot className="w-3.5 h-3.5 text-gray-500" />}
                      </div>
                      <div className={`max-w-[82%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                        msg.role === "user"
                          ? "bg-[#7B2D3F] text-white rounded-tr-sm"
                          : msg.error
                          ? "bg-red-50 text-red-700 border border-red-100 rounded-tl-sm"
                          : "bg-gray-50 text-gray-800 border border-gray-100 rounded-tl-sm"
                      }`}>
                        {msg.role === "assistant"
                          ? <RichText text={msg.content} />
                          : <span>{msg.content}</span>}
                        {msg.ts && (
                          <p className={`text-[9px] mt-1.5 ${msg.role === "user" ? "text-white/50" : "text-gray-300"}`}>
                            {new Date(msg.ts).toLocaleTimeString("ca-ES", { hour: "2-digit", minute: "2-digit" })}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                  {loading && (
                    <div className="flex gap-2.5">
                      <div className="w-7 h-7 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <Bot className="w-3.5 h-3.5 text-gray-500" />
                      </div>
                      <div className="bg-gray-50 border border-gray-100 rounded-2xl rounded-tl-sm px-4 py-3 flex items-center gap-2">
                        <div className="flex gap-1">
                          {[0, 1, 2].map(i => (
                            <span key={`member-${i}`} className="w-1.5 h-1.5 bg-gray-300 rounded-full animate-bounce"
                              style={{ animationDelay: `${i * 0.15}s` }} />
                          ))}
                        </div>
                        <span className="text-xs text-gray-400">Pensant...</span>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              )}

              {/* Quick questions */}
              {!showHistory && (
                <div className="px-3 py-2 flex gap-2 overflow-x-auto flex-shrink-0 border-t border-gray-50 scrollbar-hide">
                  <Lightbulb className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-1" />
                  <div className="flex gap-1.5 min-w-0">
                    {quickQuestions.slice(0, 5).map((q, i) => (
                      <button key={`q-${i}`} onClick={() => sendMessage(q)} disabled={loading}
                        className="flex-shrink-0 text-[11px] font-medium bg-gray-50 hover:bg-[#7B2D3F]/5 text-gray-600 hover:text-[#7B2D3F] border border-gray-200 hover:border-[#7B2D3F]/20 rounded-xl px-2.5 py-1.5 transition-all disabled:opacity-50 whitespace-nowrap">
                        {q.length > 30 ? q.slice(0, 28) + "…" : q}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Input */}
              {!showHistory && (
                <form onSubmit={(e) => { e.preventDefault(); sendMessage(input); }}
                  className="px-3 pb-3 pt-2 flex gap-2 flex-shrink-0 border-t border-gray-100">
                  <input ref={inputRef} value={input} onChange={e => setInput(e.target.value)}
                    placeholder="Escriu la teva pregunta…" disabled={loading}
                    className="flex-1 text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#7B2D3F]/20 focus:border-[#7B2D3F]/40 disabled:opacity-50 placeholder:text-gray-400" />
                  <button type="submit" disabled={loading || !input.trim()}
                    className="w-10 h-10 bg-gradient-brand rounded-xl flex items-center justify-center text-white transition-all hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </button>
                </form>
              )}
            </>
          )}

          {/* ── DRAFT TAB ── */}
          {tab === "draft" && (
            <div className="flex-1 overflow-y-auto px-4 py-3">
              <DocumentDrafter />
            </div>
          )}

          {/* ── GUIDE TAB ── */}
          {tab === "guide" && (
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">Consultes ràpides per mòdul</p>
              {GUIDE_SECTIONS.map((section) => (
                <div key={section.id} className="bg-gray-50 rounded-2xl border border-gray-100 overflow-hidden">
                  <div className={`flex items-center gap-2.5 px-4 py-3 ${section.headerBg}`}>
                    <section.icon className={`w-4 h-4 ${section.iconColor}`} />
                    <p className={`text-xs font-bold ${section.textColor}`}>{section.label}</p>
                  </div>
                  <div className="p-3 space-y-1.5">
                    {section.questions.map((q, qi) => (
                      <button key={`${section.label}-q-${qi}-${q.slice(0, 20)}`}
                        onClick={() => { sendMessage(q); setTab("chat"); }}
                        className="w-full text-left text-[11px] text-gray-600 hover:text-[#7B2D3F] bg-white hover:bg-[#f9f0f2] border border-gray-100 hover:border-[#7B2D3F]/20 rounded-xl px-3 py-2 transition-all flex items-center gap-2 group">
                        <ChevronRight className="w-3 h-3 text-gray-300 group-hover:text-[#7B2D3F] flex-shrink-0" />
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}

// ─── Guia ràpida per categories ───────────────────────────────────────────────
const GUIDE_SECTIONS = [
  {
    id: "temps",
    label: "Temperatures i APPCC",
    icon: Thermometer,
    iconColor: "text-blue-500",
    textColor: "text-blue-700",
    headerBg: "bg-blue-50",
    questions: [
      "Quina temperatura ha de tenir una nevera?",
      "Quina temperatura ha de tenir un congelador?",
      "Temperatura màxima per a menjars calents?",
      "Temperatura de recepció per a carn fresca?",
      "Quantes vegades al dia registrar temperatures?",
    ],
  },
  {
    id: "cleaning",
    label: "Neteja i desinfecció",
    icon: SprayCan,
    iconColor: "text-emerald-500",
    textColor: "text-emerald-700",
    headerBg: "bg-emerald-50",
    questions: [
      "Diferència entre neteja i desinfecció?",
      "Cada quant s'ha de netejar la cuina?",
      "Quins productes usar per desinfectar?",
      "Protocol de neteja de cambra frigorífica?",
      "Neteja de la campana extractora?",
    ],
  },
  {
    id: "incidents",
    label: "Incidències i accions correctives",
    icon: AlertTriangle,
    iconColor: "text-amber-500",
    textColor: "text-amber-700",
    headerBg: "bg-amber-50",
    questions: [
      "Com redactar una acció correctiva?",
      "Quan classificar una incidència com crítica?",
      "Mesures per contaminació creuada?",
      "Com prevenir una incidència de temperatura?",
      "Passos per gestionar un proveïdor amb problema?",
    ],
  },
  {
    id: "inspection",
    label: "Inspeccions sanitàries",
    icon: ClipboardCheck,
    iconColor: "text-violet-500",
    textColor: "text-violet-700",
    headerBg: "bg-violet-50",
    questions: [
      "Com preparar-se per a una inspecció sanitària?",
      "Quins documents han de ser accessibles?",
      "Quin és el protocol durant una inspecció?",
      "Errors més habituals en inspeccions?",
      "Quin és el termini per corregir deficiències?",
    ],
  },
  {
    id: "haccp",
    label: "Sistema APPCC",
    icon: BarChart3,
    iconColor: "text-[#7B2D3F]",
    textColor: "text-[#7B2D3F]",
    headerBg: "bg-[#f9f0f2]",
    questions: [
      "Què és el sistema APPCC?",
      "Quins punts de control crític (PCC) hi ha?",
      "Quins documents APPCC necessito?",
      "Cada quant revisar el pla APPCC?",
      "Diferència entre perill biològic, químic i físic?",
    ],
  },
  {
    id: "recipes",
    label: "Fitxes tècniques i rendibilitat",
    icon: ChefHat,
    iconColor: "text-orange-500",
    textColor: "text-orange-700",
    headerBg: "bg-orange-50",
    questions: [
      "Quins al·lèrgens cal indicar obligatòriament?",
      "Com calcular el marge de benefici d'un plat?",
      "Quin és el percentatge de cost recomanat?",
      "Com calcular la vida útil d'un producte?",
      "Informació nutricional: com calcular-la?",
    ],
  },
];